import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import Skeleton from './Skeleton';
import ContributeCTA from './ContributeCTA';
import { useAggregatedPicks } from '../hooks/useAggregatedPicks';
import { overallRankOf } from '../lib/aggregateRanking';
import { DEFAULT_PAGE_TITLE } from '../lib/siteMeta';
import type { RestaurantDetail } from '../lib/restaurantDetail';

// Shared presentation for both restaurant detail routes — /restaurant/:slug
// (name-resolved) and /place/:placeId (place_id-resolved). They differ only in
// how they look up the `RestaurantDetail`; everything a visitor sees lives here
// so the two URLs render an identical page.
//
// These pages double as SEO landing pages: a lot of visitors arrive cold from a
// search result with no idea what Rankum is. So the layout leads with a plain
// explanation of what the ranking means and ends with an on-site CTA, rather
// than only the off-site "Get Directions" action.

interface RankColors {
  text: string;
  bg: string;
  bgSubtle: string;
}

const rankColorMap: Record<number, RankColors> = {
  1: { text: 'text-amber', bg: 'bg-amber', bgSubtle: 'bg-amber/10 border-amber/30' },
  2: { text: 'text-terracotta', bg: 'bg-terracotta', bgSubtle: 'bg-terracotta/10 border-terracotta/30' },
  3: { text: 'text-clay', bg: 'bg-clay', bgSubtle: 'bg-clay/10 border-clay/30' },
};

const fallbackRankColors: RankColors = {
  text: 'text-charcoal-light',
  bg: 'bg-charcoal-light',
  bgSubtle: 'bg-sage/10 border-sage/30',
};

const getRankColors = (rank: number): RankColors => rankColorMap[rank] ?? fallbackRankColors;

const getAverageRankColor = (avgRank: number) => {
  if (avgRank <= 1.5) return 'text-amber';
  if (avgRank <= 2.5) return 'text-terracotta';
  return 'text-charcoal-light';
};

interface RestaurantDetailViewProps {
  detail: RestaurantDetail | null;
  isLoaded: boolean;
  error: string | null;
}

const RestaurantDetailView: React.FC<RestaurantDetailViewProps> = ({ detail, isLoaded, error }) => {
  // Site-wide standing ("#3 of 47") + the canonical cuisine, both read from the
  // same aggregation the homepage and the server-rendered <title> (api/page.js)
  // use — so the rank, the cuisine badge, and the tab title never disagree with
  // what crawlers see.
  const { picks: aggregatedPicks } = useAggregatedPicks();
  const aggRow = detail ? aggregatedPicks.find((p) => p.placeId === detail.placeId) : undefined;
  const overall = detail ? overallRankOf(aggregatedPicks, detail.placeId) : null;
  const cuisine = aggRow?.sampleCuisine || detail?.cuisine || '';

  // Set the document title once the detail resolves; reset on unmount. The
  // server-rendered <title> (api/page.js) is what crawlers see, but this keeps
  // the browser tab correct for visitors with JS.
  useEffect(() => {
    if (detail) {
      const hasCuisine = cuisine && cuisine !== 'Restaurant';
      document.title = hasCuisine
        ? `${detail.name}, ${cuisine} in St. Louis | Rankum`
        : `${detail.name} in St. Louis | Rankum`;
    } else if (isLoaded) {
      document.title = 'Restaurant Not Found | Rankum';
    }
    return () => {
      document.title = DEFAULT_PAGE_TITLE;
    };
  }, [detail, isLoaded, cuisine]);

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex flex-col">
        <Header backTo="/" backLabel="All Restaurants" />
        <main className="flex-1 flex items-center justify-center px-6">
          <p className="text-charcoal-light" role="alert">
            Couldn&rsquo;t load this restaurant: {error}
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-cream">
        <Header backTo="/" backLabel="All Restaurants" />
        <div className="pt-16 pb-12 md:pt-24 md:pb-16">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto" aria-busy="true" aria-label="Loading restaurant">
              <Skeleton className="h-9 w-24 rounded-full mb-6" />
              <Skeleton className="h-12 md:h-16 w-3/4 mb-6" />
              <Skeleton className="h-5 w-1/2 mb-8" />
              <Skeleton className="h-12 w-44 rounded-full mb-10" />
              <div className="flex gap-3 flex-wrap">
                <Skeleton className="h-9 w-40 rounded-full" />
                <Skeleton className="h-9 w-36 rounded-full" />
              </div>
            </div>
          </div>
        </div>
        <div className="divider mb-12"></div>
        <div className="container mx-auto px-6 pb-12">
          <div className="max-w-4xl mx-auto card p-8 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-5 w-8" />
                <Skeleton className="h-10 flex-1 rounded-lg" />
              </div>
            ))}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="min-h-screen bg-cream flex flex-col">
        <Header backTo="/" backLabel="All Restaurants" />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center animate-fade-in">
            <h1 className="font-display text-3xl text-charcoal mb-4">Restaurant not found</h1>
            <p className="text-charcoal-light mb-6">
              No one has added this place to their top five yet.
            </p>
            <Link to="/" className="text-amber hover:text-terracotta transition-colors">
              Back to all restaurants
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // `cuisine` is resolved above (aggregation-first) so it stays consistent with
  // the tab title and the server-rendered <title>.
  const { name, location, googleMapsUrl, recommendersWithRanks } = detail;
  const showCuisine = Boolean(cuisine) && cuisine !== 'Restaurant';
  const timesRecommended = recommendersWithRanks.length;
  const averageRank =
    timesRecommended === 0
      ? 0
      : recommendersWithRanks.reduce((sum, r) => sum + r.rank, 0) / timesRecommended;
  const pickerWord = timesRecommended === 1 ? 'person' : 'people';

  const rankDistribution = [1, 2, 3, 4, 5].map((rank) => ({
    rank,
    count: recommendersWithRanks.filter((r) => r.rank === rank).length,
  }));
  const maxCount = Math.max(...rankDistribution.map((r) => r.count), 1);

  return (
    <div className="min-h-screen bg-cream">
      <Header backTo="/" backLabel="All Restaurants" />

      {/* Hero */}
      <div className="pt-16 pb-12 md:pt-24 md:pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="animate-fade-in">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                {overall ? (
                  // The headline selling point: where this place stands across
                  // every St. Louis list on Rankum.
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-charcoal text-cream text-sm font-medium">
                    <svg className="w-4 h-4 text-amber" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.367 2.446a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.366-2.446a1 1 0 00-1.176 0l-3.366 2.446c-.784.57-1.838-.197-1.539-1.118l1.286-3.957a1 1 0 00-.363-1.118L2.07 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.951-.69l1.286-3.957z" />
                    </svg>
                    Ranked #{overall.rank} of {overall.total} in St. Louis
                  </span>
                ) : (
                  <span className="text-xs uppercase tracking-wider text-charcoal-light">
                    St. Louis restaurant ranking
                  </span>
                )}

                {showCuisine && (
                  <span className="px-4 py-2 rounded-full bg-amber/10 border border-amber/30 text-amber text-sm font-medium">
                    {cuisine}
                  </span>
                )}
              </div>

              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-charcoal mb-6 tracking-tight">
                {name}
              </h1>

              {location && (
                <a
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-charcoal-light hover:text-amber transition-colors duration-300 mb-6"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="link-underline">{location}</span>
                </a>
              )}

              {/* Stat badges */}
              <div className="flex items-center gap-3 flex-wrap mb-6">
                <div className="stat-badge">
                  <span className="text-amber font-semibold">{timesRecommended}</span>
                  <span className="text-charcoal-light">
                    recommendation{timesRecommended !== 1 ? 's' : ''}
                  </span>
                </div>
                {timesRecommended > 0 && (
                  <div className="stat-badge">
                    <span className={`font-semibold ${getAverageRankColor(averageRank)}`}>
                      {averageRank.toFixed(1)}
                    </span>
                    <span className="text-charcoal-light">average placement</span>
                  </div>
                )}
              </div>

              {/* Plain-language orientation for first-time visitors */}
              <p className="text-charcoal-light leading-relaxed mb-10 max-w-2xl">
                Rankum ranks the best restaurants in St. Louis using people&rsquo;s
                own top-five lists, not critic reviews or a Google Maps
                rabbit hole.{' '}
                {timesRecommended > 0 ? (
                  <>
                    <span className="text-charcoal">{name}</span> appears on{' '}
                    {/* "person"/"people" are both possessive with 's, unlike
                        the old "locals'" (which also mis-rendered "1 local'"). */}
                    {timesRecommended} {pickerWord}&rsquo;s{' '}
                    {timesRecommended === 1 ? 'list' : 'lists'}
                    {averageRank > 0 && (
                      <>, at an average placement of #{averageRank.toFixed(1)}</>
                    )}
                    .
                  </>
                ) : (
                  <>
                    Be the first to add{' '}
                    <span className="text-charcoal">{name}</span> to your top five.
                  </>
                )}
              </p>

              {/* Get Directions CTA */}
              <div>
                <a
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Get directions to ${name} on Google Maps (opens in a new tab)`}
                  className="btn-primary"
                >
                  <span>Get Directions</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="divider mb-12"></div>

      {/* Rank distribution */}
      {timesRecommended > 0 && (
        <div className="container mx-auto px-6 pb-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display text-2xl md:text-3xl text-charcoal mb-6">Rank Distribution</h2>
            <div className="card p-8">
              <div className="space-y-4">
                {rankDistribution.map(({ rank, count }) => {
                  const colors = getRankColors(rank);
                  return (
                    <div key={rank} className="flex items-center gap-4">
                      <div className="w-12 text-right">
                        <span className={`font-display text-lg ${colors.text}`}>#{rank}</span>
                      </div>
                      <div className="flex-1 h-10 bg-black/5 rounded-lg overflow-hidden relative flex items-center">
                        {count > 0 ? (
                          <div
                            className={`h-full ${colors.bg}/20 transition-all duration-500 flex items-center px-3`}
                            style={{ width: `${(count / maxCount) * 100}%`, minWidth: 'fit-content' }}
                          >
                            <span className={`text-xs sm:text-sm font-semibold ${colors.text} whitespace-nowrap`}>
                              {count} {count === 1 ? 'person' : 'people'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs sm:text-sm text-charcoal/30 px-3">0 people</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recommenders */}
      {timesRecommended > 0 && (
        <div className="container mx-auto px-6 pb-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display text-2xl md:text-3xl text-charcoal mb-6">Recommended By</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {recommendersWithRanks.map(({ recommender, rank }, index) => {
                const nameParts = recommender.name.split(' ');
                const initials = nameParts.map((n) => n[0] ?? '').join('');
                const colors = getRankColors(rank);
                return (
                  <Link
                    key={`${recommender.id}-${rank}`}
                    to={`/recommenders/${recommender.slug}`}
                    className="card p-5 group hover:shadow-lg hover:border-amber/30 transition-all duration-300"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-14 h-14 rounded-full border flex items-center justify-center flex-shrink-0 ${colors.bgSubtle}`}>
                        <span className={`font-display text-lg ${colors.text}`}>{initials}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h3 className="font-display text-lg text-charcoal group-hover:text-amber transition-colors duration-300">
                            {recommender.name}
                          </h3>
                          <div className={`flex items-center justify-center w-8 h-8 rounded-full border flex-shrink-0 ${colors.bgSubtle}`}>
                            <span className={`text-sm font-bold ${colors.text}`}>{rank}</span>
                          </div>
                        </div>
                        <p className="text-xs text-charcoal-light">
                          Ranked #{rank} · View their top 5 →
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Contribution CTA — keep cold SEO arrivals on Rankum instead of bouncing
          to Maps, and feed the flywheel (more lists → more content → more pages
          worth being found on). Shared with the homepage. */}
      <ContributeCTA secondaryLink={{ to: '/', label: 'See all St. Louis rankings' }} />

      <Footer />
    </div>
  );
};

export default RestaurantDetailView;
